import { formatUSD, formatPercent } from "@/utils/formatters";
import type { LeaderboardEntry } from "../../types";
import { MIN_VOLUME_GATE, type SectorFilter } from "./constants";

// =============================================================================
// SCORE BREAKDOWN UTILS — shared by RepBreakdownPanel (inline row expansion)
// and CalculationsDrawer (exec drawer). One source of truth for the per-rep
// journey, Federer stats, and the norm×weight component rows so the two
// surfaces can never drift apart.
// =============================================================================

// Moved verbatim from LeaderboardPage (2026-07-04) so the breakdown panel and
// the ranking math share one CPC definition.
export const sectorCPC = (entry: LeaderboardEntry, sector: SectorFilter): number => {
  // Dialer joined the setter path 2026-06-02 (Session 25): cash ÷ calls_booked,
  // with the same (shows+no_shows) proxy fallback when calls_booked is 0.
  if (sector === "setter" || sector === "dialer") {
    if (entry.calls_booked > 0) return entry.cc_total / entry.calls_booked;
    const proxy = (entry.shows || 0) + (entry.no_shows || 0);
    return proxy > 0 ? entry.cc_total / proxy : 0;
  }
  return entry.calls_taken > 0 ? entry.cc_total / entry.calls_taken : 0;
};

export interface BreakdownMaxes {
  maxCC: number;
  maxSR: number;
  maxCPC: number;
  maxCB: number;
}

// Mirrors computeWeightedRanking exactly: only reps past the sector volume gate
// set the normalization denominators, and CPC comes from sectorCPC (not the
// tooltip-only shows-denominator variant).
export const computeQualifiedMaxes = (
  entries: LeaderboardEntry[],
  sector: SectorFilter
): BreakdownMaxes => {
  const gate = MIN_VOLUME_GATE[sector];
  const qualified = entries.filter(
    (e) => Number(e[gate.field as keyof LeaderboardEntry] ?? 0) >= gate.min
  );
  const safeMax = (vals: number[]) => Math.max(...vals, 0.001);
  return {
    maxCC: safeMax(qualified.map((e) => e.cc_total || 0)),
    maxSR: safeMax(qualified.map((e) => e.show_rate || 0)),
    maxCPC: safeMax(qualified.map((e) => sectorCPC(e, sector))),
    maxCB: safeMax(qualified.map((e) => e.calls_booked || 0)),
  };
};

export interface ComponentRow {
  label: string;
  raw: string;
  norm: number;
  weight: number;
  contribution: number;
  formula: string;
  computation: string;
}

// Component rows for ONE rep using the PAGE ranking formulas (closer 50/25/25,
// setter + dialer 40/30/30 — Session 25 dialer mirror). The contributions sum
// to the entry's rank_score for ranked reps.
export const buildComponentRows = (
  entry: LeaderboardEntry,
  sector: SectorFilter,
  maxes: BreakdownMaxes
): ComponentRow[] => {
  const cpc = sectorCPC(entry, sector);
  const ccN = (entry.cc_total || 0) / maxes.maxCC;
  const srN = (entry.show_rate || 0) / maxes.maxSR;
  const cpcN = cpc / maxes.maxCPC;
  const cbN = (entry.calls_booked || 0) / maxes.maxCB;

  const orphans = (entry as { orphan_precalls?: number }).orphan_precalls ?? 0;
  const srDenom = (entry.shows || 0) + (entry.no_shows || 0) + orphans;
  const srPct = srDenom > 0 ? (((entry.shows || 0) / srDenom) * 100).toFixed(1) : "0";
  const cpcDenomLabel = sector === "closer" ? "calls_taken" : "calls_booked";
  const cpcDenom = sector === "closer" ? entry.calls_taken || 0 : entry.calls_booked || 0;

  const showRateRow = (weight: number): ComponentRow => ({
    label: "Show Rate",
    raw: formatPercent((entry.show_rate || 0) * 100),
    norm: srN,
    weight,
    contribution: srN * weight,
    formula: "Show Rate = shows / (shows + no_shows + orphan_precalls)",
    computation: `${entry.shows || 0} / (${entry.shows || 0} + ${entry.no_shows || 0} + ${orphans}) = ${srPct}%`,
  });
  const cpcRow = (weight: number): ComponentRow => ({
    label: "CPC",
    raw: formatUSD(cpc),
    norm: cpcN,
    weight,
    contribution: cpcN * weight,
    formula: `CPC = cc_total / ${cpcDenomLabel}`,
    computation: `${formatUSD(entry.cc_total)} / ${cpcDenom} = ${formatUSD(cpc)}`,
  });

  if (sector === "closer") {
    return [
      {
        label: "Cash",
        raw: formatUSD(entry.cc_total),
        norm: ccN,
        weight: 0.5,
        contribution: ccN * 0.5,
        formula: "Cash = SUM(transactions.amount) for cash-collected events",
        computation: `${formatUSD(entry.cc_total)} ÷ ${formatUSD(maxes.maxCC)} (best) = ${ccN.toFixed(2)}`,
      },
      showRateRow(0.25),
      cpcRow(0.25),
    ];
  }
  // setter + dialer share one formula (Session 25 mirror)
  return [
    cpcRow(0.4),
    showRateRow(0.3),
    {
      label: "Booked",
      raw: String(entry.calls_booked || 0),
      norm: cbN,
      weight: 0.3,
      contribution: cbN * 0.3,
      formula: "Booked = COUNT(sales_pre_call) for the rep × range",
      computation: `${entry.calls_booked || 0} ÷ ${maxes.maxCB} (best) = ${cbN.toFixed(2)}`,
    },
  ];
};

// --- Per-rep day journey (date + rank + raw + rank_points) ---
// Moved verbatim from CalculationsDrawer (2026-07-04).
export interface JourneyDay {
  date: string;
  rank: number | null;
  rank_points: number;
  cash: number;
  shows: number;
  no_shows: number;
  orphan_precalls: number;
  show_rate: number;
  cpc: number;
  calls_booked: number;
}

export function computeRepJourney(
  leaderboard: LeaderboardEntry[],
  rep_id: string,
  sector: SectorFilter
): JourneyDay[] {
  return leaderboard
    .filter((e) => e.rep_id === rep_id && e.sector === sector && e.date)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((e) => {
      const orphans = (e as { orphan_precalls?: number }).orphan_precalls ?? 0;
      const showsDenom = (e.shows || 0) + (e.no_shows || 0) + orphans;
      const computedSR = showsDenom > 0 ? (e.shows || 0) / showsDenom : 0;
      const cpcDenom = sector === "setter" ? e.calls_booked || 0 : e.shows || 0;
      const computedCPC = cpcDenom > 0 ? (e.cc_total || 0) / cpcDenom : 0;
      // show_rate from DB is 0-1 scale post-aggregation; daily rows store percent.
      // Always recompute from raw to stay consistent and avoid the 0-100 vs 0-1 trap.
      return {
        date: e.date,
        rank: e.rank ?? null,
        rank_points: e.points_earned ?? 0,
        cash: e.cc_total || 0,
        shows: e.shows || 0,
        no_shows: e.no_shows || 0,
        orphan_precalls: orphans,
        show_rate: computedSR,
        cpc: computedCPC,
        calls_booked: e.calls_booked || 0,
      };
    });
}

// --- Federer effect: who led on N of M days, tightest gap day ---
// Moved verbatim from CalculationsDrawer (2026-07-04).
export interface FedererStats {
  leadDays: number;
  totalDays: number;
  tightestGap: { date: string; myScore: number; nextScore: number; nextRep: string } | null;
}

export function computeFederer(
  leaderboard: LeaderboardEntry[],
  rep_id: string,
  sector: SectorFilter
): FedererStats {
  const dateMap = new Map<string, LeaderboardEntry[]>();
  leaderboard
    .filter((e) => e.sector === sector && e.date && e.rank != null)
    .forEach((e) => {
      const arr = dateMap.get(e.date) || [];
      arr.push(e);
      dateMap.set(e.date, arr);
    });

  let leadDays = 0;
  let totalDays = 0;
  let tightest: FedererStats["tightestGap"] = null;

  dateMap.forEach((entries, date) => {
    const sorted = entries.sort((a, b) => (b.rank_score ?? 0) - (a.rank_score ?? 0));
    const me = sorted.find((e) => e.rep_id === rep_id);
    if (!me) return;
    totalDays += 1;
    if (me.rank === 1) leadDays += 1;
    if (sorted.length >= 2 && sorted[0].rep_id === rep_id) {
      const myScore = sorted[0].rank_score ?? 0;
      const nextScore = sorted[1].rank_score ?? 0;
      const gap = myScore - nextScore;
      const tightestGap = tightest ? tightest.myScore - tightest.nextScore : Infinity;
      if (gap < tightestGap && gap >= 0) {
        tightest = {
          date,
          myScore,
          nextScore,
          nextRep: sorted[1].rep_name,
        };
      }
    }
  });

  return { leadDays, totalDays, tightestGap: tightest };
}
